import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/datetime";
import { X, ArrowLineDown, SpeakerHigh, TextAlignLeft, Sparkle, FloppyDisk, Brain, Robot, ArrowsLeftRight, ArrowSquareOut, ShieldCheck, Warning } from "@phosphor-icons/react";

const DISPOSITIONS = [
    "Interested", "Not Interested", "Callback Requested", "Left Voicemail",
    "No Answer", "Wrong Number", "Do Not Call", "Sale Closed",
];
const SPEEDS = [1, 1.5, 2];

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const sentClass = (s) => {
    const v = (s || "").toLowerCase();
    return v === "positive" ? "bg-emerald-100 text-emerald-700" : v === "negative" ? "bg-red-100 text-red-700" : "bg-stone-100 text-stone-600";
};
function fmtDur(s) {
    if (!s) return "0:00";
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, "0")}`;
}

export default function CallDetailModal({ call, onClose, onSaved, sfLink }) {
    const { orgTimezone } = useAuth();
    const audioRef = useRef(null);
    const audioCtxRef = useRef(null);
    const srcNodeRef = useRef(null);
    const [speed, setSpeed] = useState(1);
    const [transcript, setTranscript] = useState(call.transcript || "");
    const [transcribing, setTranscribing] = useState(false);
    const [transLang, setTransLang] = useState("");
    const [analysis, setAnalysis] = useState(call.ai_analysis || null);
    const [summarizing, setSummarizing] = useState(false);
    const [disposition, setDisposition] = useState(call.disposition || "");
    const [notes, setNotes] = useState(call.notes || "");
    const [saving, setSaving] = useState(false);
    const isAi = call.handled_by_ai || call.ai_analysis?.handled_by === "ai";
    const aiTurns = call.ai_transcript || [];
    const aiAnalysis = call.ai_analysis || null;

    const otherParty = call.direction === "inbound" ? call.from_number : call.to_number;
    const recUrl = call.recording_url ? `${API_BASE}/calls/${call.id}/recording` : null;

    const setRate = (r) => {
        setSpeed(r);
        if (audioRef.current) audioRef.current.playbackRate = r;
    };

    // Twilio dual-channel recordings put each party on a separate stereo channel
    // (agent left, customer right) so on headphones you only hear one party per ear.
    // Downmix stereo -> mono via Web Audio so BOTH parties are audible in BOTH earpieces.
    const enableMonoDownmix = () => {
        const el = audioRef.current;
        if (!el) return;
        if (srcNodeRef.current) {
            if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume();
            return;
        }
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            const ctx = new Ctx();
            const source = ctx.createMediaElementSource(el);
            const mono = ctx.createGain();
            mono.channelCount = 1;
            mono.channelCountMode = "explicit";
            mono.channelInterpretation = "speakers";
            source.connect(mono);
            mono.connect(ctx.destination);
            audioCtxRef.current = ctx;
            srcNodeRef.current = source;
            if (ctx.state === "suspended") ctx.resume();
        } catch (e) {
            /* graceful fallback to native (stereo) playback */
        }
    };

    useEffect(() => {
        return () => {
            if (audioCtxRef.current) {
                try { audioCtxRef.current.close(); } catch (e) { /* noop */ }
            }
        };
    }, []);

    const transcribe = async () => {
        setTranscribing(true);
        try {
            const params = new URLSearchParams();
            if (transLang) params.set("language", transLang);
            if (transcript) params.set("force", "true");
            const qs = params.toString();
            const { data } = await api.post(`/calls/${call.id}/transcribe${qs ? `?${qs}` : ""}`);
            setTranscript(data.transcript || "");
            if (!data.transcript) toast.info("No speech detected in this recording");
            else if (!data.cached) toast.success("Transcript ready");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setTranscribing(false);
        }
    };

    const summarize = async () => {
        setSummarizing(true);
        try {
            const { data } = await api.post(`/calls/${call.id}/summarize`);
            setAnalysis(data.analysis || null);
            if (!data.cached) toast.success("AI summary ready");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSummarizing(false);
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.post(`/calls/${call.id}/wrapup`, { disposition, notes });
            toast.success("Saved");
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4" data-testid="call-detail-modal" onClick={onClose}>
            <div
                className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between border-b border-stone-200 px-6 py-4">
                    <div>
                        <div className="font-heading text-xl font-bold tracking-tight">{otherParty || "—"}</div>
                        <div className="mt-0.5 text-xs text-stone2">
                            {call.direction === "inbound" ? "Inbound" : "Outbound"} · {call.user_name || "—"} · {fmtDur(call.duration_sec)} · {formatDateTime(call.created_at, orgTimezone)}
                        </div>
                        {call.transferred_from && (
                            <span
                                data-testid="call-detail-transfer-badge"
                                className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
                            >
                                <ArrowsLeftRight size={11} weight="bold" />
                                Transferred: {call.transferred_from} → {call.transferred_to || "—"}
                            </span>
                        )}
                    </div>
                    <button onClick={onClose} data-testid="call-detail-close" className="rounded-full p-1.5 text-stone2 hover:bg-stone-100 hover:text-ink">
                        <X size={20} />
                    </button>
                </div>
                <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
                    {isAi && (
                        <>
                            {aiTurns.length > 0 && (
                                <div>
                                    <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                        <Robot size={14} weight="fill" /> AI Conversation
                                    </div>
                                    <div data-testid="ai-conversation" className="max-h-60 space-y-2 overflow-y-auto rounded-xl border border-stone-200 bg-stone-50 p-4">
                                        {aiTurns.map((t, i) => (
                                            <div key={i} className={t.role === "caller" ? "text-right" : "text-left"}>
                                                <span className={`inline-block max-w-[85%] rounded-2xl px-3 py-1.5 text-sm leading-relaxed ${t.role === "caller" ? "bg-brand/15 text-ink" : "border border-stone-200 bg-white text-ink"}`}>
                                                    <span className="mr-1 text-[10px] font-bold uppercase tracking-wider text-stone2">{t.role === "caller" ? "Caller" : "AI"}</span>
                                                    {t.text}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div>
                                <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                    <Brain size={14} weight="fill" /> AI Summary
                                </div>
                                {aiAnalysis ? (
                                    <div data-testid="ai-call-analysis" className="space-y-3 rounded-xl border border-stone-200 bg-white p-4 text-sm text-ink">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span data-testid="ai-sentiment-badge" className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${sentClass(aiAnalysis.sentiment)}`}>
                                                {cap(aiAnalysis.sentiment) || "Neutral"}
                                            </span>
                                            {aiAnalysis.resolution && <span className="rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-stone-600">{aiAnalysis.resolution}</span>}
                                            {aiAnalysis.disposition && <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-700">{aiAnalysis.disposition}</span>}
                                        </div>
                                        {aiAnalysis.summary && <p className="leading-relaxed">{aiAnalysis.summary}</p>}
                                        {(aiAnalysis.actions || []).length > 0 && (
                                            <div className="flex flex-wrap gap-1.5">
                                                {aiAnalysis.actions.map((a, i) => (
                                                    <span key={i} className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">{a.kind}{a.ref ? ` · ${a.ref}` : ""}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="rounded-xl border border-dashed border-stone-300 p-4 text-sm text-stone2">No AI summary was generated for this call.</div>
                                )}
                            </div>
                        </>
                    )}
                    {!isAi && (<>
                    {/* Player */}
                    {recUrl ? (
                        <div className="rounded-xl border border-stone-200 bg-stone-50 p-4">
                            <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                <SpeakerHigh size={14} weight="fill" /> Recording
                            </div>
                            <audio
                                ref={audioRef}
                                data-testid="call-detail-audio"
                                src={recUrl}
                                controls
                                className="h-10 w-full"
                                onPlay={enableMonoDownmix}
                                onLoadedMetadata={() => { if (audioRef.current) audioRef.current.playbackRate = speed; }}
                                onError={() => { if (call.provider !== "mock") toast.error("Couldn't load this recording"); }}
                            />
                            <div className="mt-3 flex items-center gap-2">
                                <span className="text-xs text-stone2">Speed</span>
                                {SPEEDS.map((r) => (
                                    <button
                                        key={r}
                                        data-testid={`speed-${r}x`}
                                        onClick={() => setRate(r)}
                                        className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
                                            speed === r ? "bg-ink text-white" : "border border-stone-300 text-stone2 hover:border-ink hover:text-ink"
                                        }`}
                                    >
                                        {r}x
                                    </button>
                                ))}
                                <a
                                    href={recUrl}
                                    download={`call-${call.id}.mp3`}
                                    data-testid="call-detail-download"
                                    className="ml-auto inline-flex items-center gap-1 rounded-full border border-stone-300 px-3 py-1 text-xs font-semibold text-stone2 hover:border-ink hover:text-ink"
                                >
                                    <ArrowLineDown size={14} /> Download
                                </a>
                            </div>
                        </div>
                    ) : call.recording_health === "unavailable" ? (
                        <div data-testid="recording-health-warning" className="flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                            <span className="mt-0.5 shrink-0"><Warning size={18} weight="fill" className="text-amber-600" /></span>
                            <div>
                                <div className="font-semibold">Recording unavailable</div>
                                <div className="mt-0.5 text-amber-800">This call was set to record, but the recording never became downloadable — it likely failed to upload from your provider. No action needed on your side; future calls are unaffected.</div>
                            </div>
                        </div>
                    ) : call.recording_health === "pending" ? (
                        <div data-testid="recording-health-pending" className="rounded-xl border border-dashed border-stone-300 p-4 text-sm text-stone2">Recording is still being prepared by your provider — check back in a few minutes.</div>
                    ) : (
                        <div className="rounded-xl border border-dashed border-stone-300 p-4 text-sm text-stone2">No recording for this call.</div>
                    )}

                    {/* Transcript */}
                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                <TextAlignLeft size={14} /> Transcript
                            </div>
                            {recUrl && (
                                <div className="flex items-center gap-2">
                                    <select
                                        data-testid="transcribe-language"
                                        value={transLang}
                                        onChange={(e) => setTransLang(e.target.value)}
                                        title="Language spoken on the call — pick one for accurate non-English transcripts"
                                        className="rounded-full border border-stone-300 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-ink"
                                    >
                                        <option value="">Auto-detect</option>
                                        <option value="en">English</option>
                                        <option value="bn">Bengali</option>
                                        <option value="hi">Hindi</option>
                                        <option value="ta">Tamil</option>
                                        <option value="te">Telugu</option>
                                        <option value="mr">Marathi</option>
                                        <option value="gu">Gujarati</option>
                                        <option value="kn">Kannada</option>
                                        <option value="ml">Malayalam</option>
                                        <option value="pa">Punjabi</option>
                                        <option value="ur">Urdu</option>
                                    </select>
                                    <button
                                        onClick={transcribe}
                                        disabled={transcribing}
                                        data-testid="transcribe-button"
                                        className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-ink transition-all hover:-translate-y-0.5 disabled:opacity-50"
                                    >
                                        <Sparkle size={14} weight="fill" />
                                        {transcribing ? "Transcribing…" : transcript ? "Re-run" : "Transcribe"}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div
                            data-testid="transcript-text"
                            className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-xl border border-stone-200 bg-white p-4 text-sm leading-relaxed text-ink"
                        >
                            {transcribing ? (
                                <span className="text-stone2">Converting speech to text…</span>
                            ) : transcript ? (
                                transcript
                            ) : (
                                <span className="text-stone2">No transcript yet. Click “Transcribe” to generate one.</span>
                            )}
                        </div>
                    </div>

                    {/* AI summary & sentiment */}
                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                <Brain size={14} weight="fill" /> AI Summary &amp; Sentiment
                            </div>
                            <button
                                onClick={summarize}
                                disabled={summarizing || !transcript}
                                title={!transcript ? "Transcribe the call first" : ""}
                                data-testid="summarize-button"
                                className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-40"
                            >
                                <Sparkle size={14} weight="fill" />
                                {summarizing ? "Analyzing…" : analysis ? "Re-run" : "Generate"}
                            </button>
                        </div>
                        <div
                            data-testid="ai-analysis"
                            className="rounded-xl border border-stone-200 bg-white p-4 text-sm text-ink"
                        >
                            {summarizing ? (
                                <span className="text-stone2">Analyzing the conversation…</span>
                            ) : analysis ? (
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <span
                                            data-testid="sentiment-badge"
                                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${
                                                analysis.sentiment === "Positive"
                                                    ? "bg-emerald-100 text-emerald-700"
                                                    : analysis.sentiment === "Negative"
                                                    ? "bg-red-100 text-red-700"
                                                    : "bg-stone-100 text-stone-600"
                                            }`}
                                        >
                                            <span className={`h-1.5 w-1.5 rounded-full ${
                                                analysis.sentiment === "Positive" ? "bg-emerald-500"
                                                : analysis.sentiment === "Negative" ? "bg-red-500" : "bg-stone-400"
                                            }`} />
                                            {analysis.sentiment}
                                        </span>
                                        {analysis.sentiment_reason && (
                                            <span className="text-xs text-stone2">{analysis.sentiment_reason}</span>
                                        )}
                                    </div>
                                    {analysis.summary && <p className="leading-relaxed">{analysis.summary}</p>}
                                    {analysis.key_points?.length > 0 && (
                                        <ul className="ml-4 list-disc space-y-1 text-sm text-stone-700">
                                            {analysis.key_points.map((k, i) => <li key={i}>{k}</li>)}
                                        </ul>
                                    )}
                                </div>
                            ) : (
                                <span className="text-stone2">
                                    {transcript ? "Click “Generate” for an AI summary, sentiment and key points." : "Transcribe the call first, then generate the AI summary."}
                                </span>
                            )}
                        </div>
                    </div>
                    </>)}

                    {call.handled_by_ai && analysis?.actions?.length > 0 && (
                        <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4" data-testid="ai-actions">
                            <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-violet-700">
                                <Robot size={14} weight="fill" /> Assistant actions
                            </div>
                            <ul className="space-y-1.5">
                                {analysis.actions.map((a, i) => (
                                    <li key={i} data-testid={`ai-action-${i}`} className="flex flex-wrap items-center gap-2 text-sm">
                                        <span className="inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700">{(a.kind || "action").replace(/_/g, " ")}</span>
                                        {a.ref && <span className="font-mono text-xs text-ink">{a.ref}</span>}
                                        {a.emailed && <span className="text-[11px] font-semibold text-emerald-600">✉ confirmation emailed</span>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {call.handled_by_ai && analysis?.blocked_duplicates?.length > 0 && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4" data-testid="ai-duplicate-guard">
                            <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-700">
                                <ShieldCheck size={14} weight="fill" /> Duplicate guard · {analysis.blocked_duplicates.length} blocked
                            </div>
                            <p className="mb-2 text-[12px] leading-snug text-amber-800">The assistant tried to repeat an action it had already completed on this call — it was blocked, so no duplicate record or email was created.</p>
                            <ul className="space-y-1.5">
                                {analysis.blocked_duplicates.map((b, i) => (
                                    <li key={i} data-testid={`ai-duplicate-${i}`} className="flex flex-wrap items-center gap-2 text-sm">
                                        <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">{(b.kind || "action").replace(/_/g, " ")}</span>
                                        {b.ref && <span className="font-mono text-xs text-ink">{b.ref}</span>}
                                        <span className="text-[11px] font-semibold text-amber-600">repeat blocked</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}


                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Disposition</label>
                            <select
                                data-testid="detail-disposition"
                                value={disposition}
                                onChange={(e) => setDisposition(e.target.value)}
                                className="mt-2 w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink"
                            >
                                <option value="">— Select —</option>
                                {DISPOSITIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                        <div className="sm:row-span-1">
                            <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Notes</label>
                            <textarea
                                data-testid="detail-notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={3}
                                className="mt-2 w-full resize-none rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink"
                                placeholder="Call notes…"
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-6 py-4">
                    {sfLink && (
                        <a
                            href={sfLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="call-detail-sf-open"
                            className="mr-auto inline-flex items-center gap-1.5 rounded-full border border-sky-300 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-800 transition-all hover:-translate-y-0.5 hover:bg-sky-100"
                        >
                            <ArrowSquareOut size={16} weight="bold" /> Open in Salesforce
                        </a>
                    )}
                    <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-semibold text-stone2 hover:bg-stone-100">Close</button>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="detail-save"
                        className="inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    >
                        <FloppyDisk size={16} /> {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
